/**
 * 家长录音（覆盖 TTS 朗读）—— `MediaRecorder` 的封装。
 *
 * ## 为什么需要它（这一步是整个听写模块唯一能纠正读音的地方）
 *
 * `speechSynthesis` **只能读文字**，多音字/轻声它一定会读错，而"读错音"就是
 * **把孩子教错**。第一步的"人工覆盖拼音"解决的是显示与数据，解决不了朗读。
 * 所以每个词都要允许家长录一遍自己的声音来覆盖 TTS。
 *
 * ## 格式与体积（与后端同一条口径，见 `shared/dictation/recording.ts`）
 *
 * | 项 | 取值 |
 * | --- | --- |
 * | 首选格式 | `audio/webm;codecs=opus`（Chrome/Edge/Android 都支持，体积最小） |
 * | 退路 | `audio/mp4`（Safari/iOS 只给它）、`audio/ogg;codecs=opus`（Firefox） |
 * | 上限 | base64 ≤ 400 000 个字符（≈ 300 KB 音频），与数据库 CHECK 同一个常量 |
 * | 提醒线 | base64 ≥ 300 000（上限的 75%）时界面变黄，提示"有点长" |
 * | 建议时长 | 20 秒（多音字一个词 1~3 秒就够，20 秒是给成语留的余量） |
 *
 * ⚠️ 判定**必须在浏览器里、上传之前**做：上传超限时 Fastify 的 `bodyLimit`（1 MB）
 *    会在**路由之前**把请求挡掉，家长看到的是一句笼统的"请求格式有误"，
 *    完全不知道是"录音太长"。
 *
 * ## ⚠️ 为什么不做"边录边传"或"直接传 Blob"
 *
 * 本项目**没有任何文件存储基础设施**（没有 multer、没有上传卷、worker 也不写文件），
 * 所以音频以 base64 存进 Postgres（`dictation_word_audio.audio_base64`，见 schema.ts）。
 * 因此这里必须把 Blob 读成 base64 —— `FileReader.readAsDataURL` 给出的
 * `data:...;base64,XXXX` 要把前缀剥掉再传（后端**显式拒绝**带前缀的值，
 * 因为那类错误会导致"存进去了但放不出声"）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DICTATION_AUDIO_MAX_BASE64,
  DICTATION_AUDIO_SUGGESTED_MAX_MS,
  base64DecodedBytes,
  classifyAudioSize,
  type AudioSizeVerdict,
} from '@fht/shared';

/** 候选格式，**按优先级**（第一个 `isTypeSupported` 的胜出）。 */
const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  'audio/mpeg',
] as const;

/** 浏览器支持哪一种录音格式；都不支持时返回 `null`。 */
export function pickRecorderMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of CANDIDATE_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch {
      /* 某些实现对不认识的串会抛，跳过 */
    }
  }
  // 一个都不认时用系统默认（`new MediaRecorder(stream)` 不带 mimeType）
  return '';
}

/** `data:audio/webm;codecs=opus;base64,AAAA` → `AAAA`（后端要的是纯 base64）。 */
export function stripDataUrlPrefix(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
}

/** base64 长度 → 原始字节数（估算；真实大小以 `Blob.size` 为准）。 */
export function estimateBytesFromBase64(base64: string): number {
  return base64DecodedBytes(base64.length);
}

/** 一段录好的音频。 */
export interface RecordedClip {
  /** 纯 base64（无 `data:` 前缀）。 */
  base64: string;
  mimeType: string;
  /** `Blob.size`（原始字节数）。 */
  bytes: number;
  /** 真实时长（毫秒）；浏览器没给就是 `null`（**不是 0**）。 */
  durationMs: number | null;
  /** 当前可播放的预览 URL（`URL.createObjectURL` 出来的，换一段要 revoke）。 */
  previewUrl: string;
  /** 体积判定（与后端同一条口径）。 */
  verdict: AudioSizeVerdict;
  /** 超过建议时长（20 秒）。 */
  tooLong: boolean;
}

export type RecorderStatus = 'idle' | 'recording' | 'ready' | 'error';

export interface UseDictationRecorderResult {
  status: RecorderStatus;
  /** 当前已录的毫秒数（录音中每秒刷新，界面上显示"已录 3 秒"）。 */
  elapsedMs: number;
  clip: RecordedClip | null;
  /** 录音格式（`''` = 浏览器默认）。 */
  mimeType: string | null;
  /** 浏览器支不支持录音（不支持时界面要说清楚，而不是给一个点了没反应的按钮）。 */
  supported: boolean;
  /** 给家长看的一句话（权限被拒、设备没有麦克风、体积超限……）。 */
  error: string | null;
  /** 当前码率（bit/s）与"按这个码率还能录多少秒"的估计 —— 用来显示体积预算。 */
  bitsPerSecond: number | null;
  start: () => Promise<void>;
  stop: () => void;
  /** 丢掉当前这一段（清空预览）。 */
  reset: () => void;
}

/**
 * 录音 hook。
 *
 * 生命周期：`start()` 申请麦克风并开始录 → `stop()` 结束并产出 `clip`。
 * 组件卸载 / 换词时**必须**把流与预览 URL 都释放掉：`getUserMedia` 的流不关
 * 会让手机上的"正在录音/麦克风使用中"指示灯一直亮着。
 */
export function useDictationRecorder(): UseDictationRecorderResult {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [clip, setClip] = useState<RecordedClip | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bitsPerSecond, setBitsPerSecond] = useState<number | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const supported = typeof MediaRecorder !== 'undefined' && typeof navigator !== 'undefined' && navigator.mediaDevices !== undefined;

  const releasePreview = useCallback(() => {
    if (previewUrlRef.current !== null) {
      try {
        URL.revokeObjectURL(previewUrlRef.current);
      } catch {
        /* 忽略 */
      }
      previewUrlRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const stream = streamRef.current;
    if (stream !== null) {
      for (const track of stream.getTracks()) {
        try {
          track.stop();
        } catch {
          /* 忽略 */
        }
      }
      streamRef.current = null;
    }
    recorderRef.current = null;
  }, []);

  const reset = useCallback(() => {
    releaseStream();
    releasePreview();
    chunksRef.current = [];
    startedAtRef.current = null;
    setClip(null);
    setElapsedMs(0);
    setBitsPerSecond(null);
    setError(null);
    setStatus('idle');
  }, [releasePreview, releaseStream]);

  useEffect(
    () => () => {
      releaseStream();
      releasePreview();
    },
    [releasePreview, releaseStream],
  );

  const start = useCallback(async () => {
    if (!supported) {
      setError('这台设备/浏览器不支持录音，可以先跳过这个词（它仍然会用浏览器语音朗读）。');
      setStatus('error');
      return;
    }
    // 重录：先把上一段的资源放掉（否则手机上会同时持有两个流）
    releasePreview();
    releaseStream();
    setClip(null);
    setElapsedMs(0);
    setBitsPerSecond(null);
    setError(null);
    chunksRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as { name?: string }).name ?? '';
      setError(
        name === 'NotAllowedError'
          ? '浏览器没有拿到麦克风权限。请在地址栏的权限设置里允许麦克风后再试。'
          : name === 'NotFoundError'
            ? '这台设备没有找到麦克风。'
            : '打不开麦克风，请检查设备与权限。',
      );
      setStatus('error');
      return;
    }

    const mimeType = pickRecorderMimeType();
    if (mimeType === null) {
      for (const track of stream.getTracks()) track.stop();
      setError('这台浏览器不支持录音（MediaRecorder 不可用）。');
      setStatus('error');
      return;
    }

    streamRef.current = stream;
    const recorder =
      mimeType === '' ? new MediaRecorder(stream) : new MediaRecorder(stream, { mimeType });
    recorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType !== '' ? recorder.mimeType : (mimeType || 'audio/webm');
      const blob = new Blob(chunksRef.current, { type });
      const startedAt = startedAtRef.current;
      startedAtRef.current = null;
      releaseStream();

      if (blob.size === 0) {
        setError('这一段没有录到声音，请重新录一遍。');
        setStatus('error');
        return;
      }

      const durationMs = startedAt === null ? null : Math.max(0, Date.now() - startedAt);
      const previewUrl = URL.createObjectURL(blob);
      previewUrlRef.current = previewUrl;
      setBitsPerSecond(durationMs !== null && durationMs > 0 ? (blob.size * 8 * 1000) / durationMs : null);

      /**
       * ⚠️ 读成 base64 是**异步**的，所以整个"产出 clip"也在异步回调里。
       *    读失败（极少见）也要给人话，而不是让界面永远停在"正在处理"。
       */
      const reader = new FileReader();
      reader.onload = () => {
        const raw = typeof reader.result === 'string' ? reader.result : '';
        const base64 = stripDataUrlPrefix(raw);
        const verdict = classifyAudioSize({
          base64Length: base64.length,
          bytes: blob.size,
        });
        setClip({
          base64,
          mimeType: type,
          bytes: blob.size,
          durationMs,
          previewUrl,
          verdict,
          tooLong: durationMs !== null && durationMs > DICTATION_AUDIO_SUGGESTED_MAX_MS,
        });
        setStatus(verdict.code === 'error' ? 'error' : 'ready');
        setError(verdict.code === 'error' ? verdict.message : null);
      };
      reader.onerror = () => {
        setError('读取录音数据失败，请重新录一遍。');
        setStatus('error');
      };
      reader.readAsDataURL(blob);
    };

    startedAtRef.current = Date.now();
    recorder.start();
    setStatus('recording');
    // 每 200ms 刷新一次"已录多久"（界面上还要显示体积预算）
    tickRef.current = setInterval(() => {
      setElapsedMs(startedAtRef.current === null ? 0 : Date.now() - startedAtRef.current);
    }, 200);
  }, [releasePreview, releaseStream, supported]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder !== null && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        releaseStream();
        setError('结束录音时出错了，请重新录一遍。');
        setStatus('error');
      }
    } else {
      releaseStream();
    }
  }, [releaseStream]);

  return {
    status,
    elapsedMs,
    clip,
    mimeType: clip?.mimeType ?? pickRecorderMimeType(),
    supported,
    error,
    bitsPerSecond,
    start,
    stop,
    reset,
  };
}

/** 上限（base64 字符数）常量再导出：界面显示"还能录多久"时要用。 */
export { DICTATION_AUDIO_MAX_BASE64, DICTATION_AUDIO_SUGGESTED_MAX_MS };
